import dynamic from 'next/dynamic';
import React, {
  Suspense, useRef, useState, useEffect, useLayoutEffect,
} from 'react';
import { nanoid } from 'nanoid';

import Script from 'next/script';
import Head from '@/core/head';
import { useTranslation, lang } from '@/helpers/translate';
import { useSearchZustandStore } from '@/helpers/searchctx.js';
import { useZusTrackerStore } from '@/helpers/tracker.js';
import Icon from '@/core/icon.js';
import { ucfirst, isClient } from '@/helpers/common';
import { getRootUrl } from '@/helpers/router';

import Results from '@/components/search/results.js';
import SearchEvent from '@/components/search/events/search.js';
import SpaceEvent from '@/components/search/events/space.js';
import Map from '@/components/search/map.js';
import SearchPageComponent from '@/components/header/searchPageSearch.js';
import { UseZusModalStore } from '@/helpers/modal.js';

import Filters from '@/components/search/filters.js';
import MarkerPinEvent from '@/components/search/events/markerpin.js';
import FiltersEvents from '@/components/search/events/filters.js';
import MapEvent from '@/components/search/events/map.js';

function ButtonsResponsive({ mapRef }) {
  const { t } = useTranslation();
  const [type, setType] = useState({ slug: 'results' });
  const [isMapVisible, setIsMapVisible] = useState(false);

  useEffect(() => {
    setIsMapVisible(type.slug === 'map' && type.changeId);
  }, [type.changeId, type.slug]);

  useLayoutEffect(() => {
    const mapElement = mapRef.current;
    const overflowStyle = isMapVisible ? 'hidden' : 'scroll';
    const zIndexClass = 'z-20';

    if (mapElement) {
      if (isMapVisible) {
        mapElement.classList.add(zIndexClass);
      } else {
        mapElement.classList.remove(zIndexClass);
      }
    }

    document.documentElement.style.overflow = overflowStyle;
    document.body.style.overflow = overflowStyle;

    return () => {
      document.documentElement.style.overflow = 'auto';
      document.body.style.overflow = 'auto';
    };
  }, [isMapVisible]);

  const [prevScrollPos, setPrevScrollPos] = useState(0);
  const [visible, setVisible] = useState(true);

  const handleScroll = () => {
    const currentScrollPos = window.pageYOffset;
    setVisible(prevScrollPos > currentScrollPos || currentScrollPos < 5);
    setPrevScrollPos(currentScrollPos);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [prevScrollPos, visible, handleScroll]);

  let containerClass = 'transition-all cursor-pointer lg:hidden z-20 fixed left-1/2 transform -translate-x-1/2 bg-black rounded-full shadow-button px-4 py-2 flex items-center';

  if (visible) {
    containerClass += ' bottom-20';
  }

  if (!visible) {
    containerClass += ' bottom-4';
  }

  function onClickResponsive(slug) {
    setType({ slug, changeId: nanoid() });
  }

  return type.slug === 'results' ? (
    <div className={containerClass} onClick={() => { onClickResponsive('map'); }}>
      <span className="mr-3 text-sm text-white whitespace-nowrap">{t('search:responsive.map')}</span>
      <Icon className="w-6 h-6 text-white fill-current" name="map" />
    </div>
  ) : (
    <div className={containerClass} onClick={() => { onClickResponsive('results'); }}>
      <span className="mr-3 text-sm text-white whitespace-nowrap">{t('search:responsive.list')}</span>
      <Icon className="w-6 h-6 text-white fill-current" name="list" />
    </div>
  );
}

function SearchHeader({ initParams: _initParams }) {
  const zusinitParams = useSearchZustandStore((state) => state.initParams);
  const initParams = !isClient() ? _initParams : zusinitParams;

  const { t } = useTranslation();
  const langCurrent = lang();
  const { setOrEvent } = useZusTrackerStore.getState();

  useEffect(() => {
    setOrEvent('search_page');
  }, []);

  return (
    <Head>
      <title>{t('search:metatitle.production', { location: ucfirst(initParams?.title), page: !initParams?.page ? '' : `- Page ${initParams?.page}` })}</title>
      <link rel="canonical" href={`${process.env.NEXT_PUBLIC_URL_V4}${getRootUrl(langCurrent)}/s/${initParams?.location}`} />
      {/* Alternates sur /s/<location> préfixé par langue (comme le canonical) : l'ancien
          `pathname` contenait déjà le préfixe de locale sur /fr et /es, ce qui produisait
          des hreflang vers /fr/fr/s/… (404) et étiquetait la page FR comme `en`. */}
      <link rel="alternate" hrefLang="x-default" href={`${process.env.NEXT_PUBLIC_URL_V4}${getRootUrl('fr')}/s/${initParams?.location}`} />
      {['en', 'fr', 'es'].map((langlang) => (
        <link
          key={langlang}
          rel="alternate"
          hrefLang={langlang}
          href={`${process.env.NEXT_PUBLIC_URL_V4}${getRootUrl(langlang)}/s/${initParams?.location}`}
        />
      ))}
      <meta name="description" content={t('search:metadesc.production', { location: ucfirst(initParams?.title), page: !initParams?.page ? '' : `- Page ${initParams?.page}` })} />
    </Head>
  );
}

function Search({
  filters, districts, spaces, initParams,
}) {
  const mapRef = useRef();
  const mapRef2 = useRef();
  useSearchZustandStore.setState({ mapRef });

  useEffect(() => () => {
    // console.log('SEARCH PAGE UMOUNT');
    useSearchZustandStore.setState({
      spacesData: [],
      varGraphql: {},
      initializedMap: false,
    });
    const { setModal1 } = UseZusModalStore.getState();
    setModal1(null);
  }, []);

  return (
    <>
      <SearchHeader {...{ initParams }} />
      <div className="filters-light absolute top-0 lg:static bg-white lg:pb-1 lg:overflow-hidden lg:overscroll-none">
        <Suspense fallback={<div> </div>}>
          <div className="lg:mx-5 h-[4.3em] lg:h-[3.4em] lg:mt-19 2xl:max-w-[1800px]"><Filters {...{ filters }} /></div>
        </Suspense>
        <div className="z-20 bg-white/95 backdrop-blur-sm shadow-[0_1px_0_rgba(19,14,27,0.05)] pl-4 fixed top-0 left-0 right-0 h-[4.3em] lg:hidden">
          <SearchPageComponent />
        </div>
      </div>
      <div id="contentSearchList2256" className="flex w-full h-full md:static lg:h-full lg:pt-0 lg:overflow-y-visible lg:fixed lg:top-[132px] lg:rounded-t-none lg:grid lg:grid-cols-10">
        {/* WEA-157 — hydratation : on passe TOUJOURS les spaces rendus côté serveur. Avant, le
             serveur rendait <Results space={...}/> (les 24 cartes) et le client <Results/> sans la
             prop, donc une grille vide au premier rendu : React constatait la différence
             ("Did not expect server HTML to contain a <div> in <div>"), jetait le HTML serveur de
             cette colonne et la re-rendait côté client — ce qui lui faisait perdre son
             interactivité par intermittence. Les recherches suivantes viennent du store, comme
             avant (cf. useEffect dans results.js). */}
        <Suspense fallback={<div />}>
          <Results space={spaces?.results} />
        </Suspense>
        <div className="fixed bg-purple-0 top-0 bottom-0 order-first w-full overflow-hidden h-dvh lg:order-none md:-left-3 lg:rounded-none md:h-full lg:col-span-3 lg:top-0 md:w-[calc(100%_+_1rem)] lg:relative" ref={mapRef}>
          <Suspense fallback={<div />}>
            <Map {...{ districts }} ref={mapRef2} />
          </Suspense>
        </div>
        <ButtonsResponsive {...{ mapRef }} />
        <div className="hidden">
          <Suspense fallback={<div />}>
            <SearchEvent />
            <MapEvent />
            <SpaceEvent />
            <MarkerPinEvent />
            <FiltersEvents />
          </Suspense>
        </div>
      </div>
    </>
  );
}

export default Search;
